import { gql, useMutation } from '@apollo/client';
import * as React from 'react';

import AdminSelectUser, { type IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import DangerConfirmDialog from '../../components/DangerConfirmDialog';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';

import type {
  AdminDeleteAccountMutation,
  AdminDeleteAccountMutationVariables,
} from './__generated__/DeleteAccountScene.gql';

const ADMIN_DELETE_ACCOUNT = gql`
  mutation AdminDeleteAccount($userId: ID!) {
    adminDeleteAccount(userId: $userId)
  }
`;

/**
 * Delete Account admin tool. Operator selects a user, then hard-deletes the
 * account: the mutation tombstones the user as `DELETED` (blocking login) and
 * dispatches the background purge (Plaid revoke + cascade family delete).
 * Because the blast radius is the entire family with no undo, the confirm
 * dialog runs in typed mode — the operator must type the user's email
 * verbatim before the delete enables.
 */
const DeleteAccountScene = () => {
  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const { showToast } = useToast();

  const [deleteAccount] = useMutation<AdminDeleteAccountMutation, AdminDeleteAccountMutationVariables>(
    ADMIN_DELETE_ACCOUNT
  );

  const handleUserSelect = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    setConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!selection) {
      return;
    }
    // Let rejection propagate so DangerConfirmDialog stays open for a retry;
    // the Apollo errorLink reports the failure to Sentry.
    await deleteAccount({ variables: { userId: selection.userId } });
    showToast({ type: 'success', message: `Account deletion started for ${selection.email}.` });
    setSelection(null);
  };

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-6 px-6 py-5">
        <div className="flex flex-col gap-1">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Delete account</h1>
          <p className="text-fg-secondary text-xs">
            Select a user to permanently delete their account — tombstones the user and purges their family (banks,
            transactions, bills) in the background. There is no undo.
          </p>
        </div>

        <div className="w-full max-w-md">
          <AdminSelectUser selectedUserId={selection?.userId ?? null} onSelectionChange={handleUserSelect} />
        </div>

        {!selection ? (
          <div className="text-fg-tertiary text-xs italic">Select a user to delete their account.</div>
        ) : (
          <div className="border-fg-error-primary bg-bg-error-primary/10 flex max-w-md flex-col gap-3 border px-4 py-3">
            <div className="flex flex-col gap-1 text-xs">
              <span className="text-fg-error-primary font-semibold uppercase tracking-widest">Danger zone</span>
              <span className="text-fg-secondary">
                Deleting <span className="text-fg-primary font-mono">{selection.email}</span> revokes their Plaid
                connections and hard-deletes their family and all associated data.
              </span>
              <span className="text-fg-tertiary font-mono text-[11px]">user {selection.userId}</span>
            </div>
            <div>
              <Button color="danger" size="sm" onClick={() => setConfirmOpen(true)}>
                Delete account
              </Button>
            </div>
          </div>
        )}

        <DangerConfirmDialog
          isOpen={confirmOpen}
          onOpenChange={setConfirmOpen}
          confirmValue={selection?.email}
          title="Delete account"
          body={
            <div className="flex flex-col gap-2">
              <div>
                This permanently deletes <span className="font-mono">{selection?.email}</span> and their entire family —
                bank connections, transactions, and bills. This cannot be undone.
              </div>
              <div className="text-fg-secondary text-xs">
                The user is blocked from logging in immediately; the background purge removes the data shortly after.
              </div>
            </div>
          }
          confirmLabel="Delete account"
          onConfirm={handleConfirmDelete}
        />
      </main>
    </div>
  );
};

export default DeleteAccountScene;
